import React from 'react';
import { useAuth } from '@soap/modules';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Switch } from '../../ui/switch';
import {
    dedupeMaterialsById,
    getPrintableMaterials,
    resolveMaterialsForStep
} from '../InProgressUnitsPanel/instructionStepUtils';
import {
    buildAllInstructionStepsPrintHtml,
    buildInstructionStepPrintHtml,
    printInstructionStepDocument
} from '../InProgressUnitsPanel/printInstructionStepDocument';
import type { InstructionStepDto, SupportingMaterialDto } from '../InProgressUnitsPanel/types';

interface LessonPrintWizardDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    steps: InstructionStepDto[];
    currentStep?: InstructionStepDto;
    supportingMaterials?: SupportingMaterialDto[];
    lessonSteps?: InstructionStepDto[];
}

export function LessonPrintWizardDialog({
    open,
    onOpenChange,
    title,
    steps,
    currentStep,
    supportingMaterials,
    lessonSteps
}: LessonPrintWizardDialogProps) {
    const allLessonSteps = lessonSteps ?? steps;
    const canPrintCurrentStep = Boolean(currentStep);
    const { idToken } = useAuth('lesson-print-wizard');

    const [printAllSteps, setPrintAllSteps] = React.useState(true);
    const [printStepNotes, setPrintStepNotes] = React.useState(true);
    const [pageBreaksBetweenItems, setPageBreaksBetweenItems] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

    const printable = React.useMemo(() => {
        if (printAllSteps || !currentStep) {
            const collected = steps.flatMap(step =>
                resolveMaterialsForStep(step, supportingMaterials, allLessonSteps, {
                    sliceWorksheets: false
                })
            );
            return dedupeMaterialsById(getPrintableMaterials(collected));
        }
        return getPrintableMaterials(
            resolveMaterialsForStep(currentStep, supportingMaterials, allLessonSteps, {
                sliceWorksheets: true
            })
        );
    }, [printAllSteps, currentStep, steps, supportingMaterials, allLessonSteps]);

    React.useEffect(() => {
        if (!open) return;
        setPrintAllSteps(true);
        setPrintStepNotes(true);
        setPageBreaksBetweenItems(false);
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        setSelectedIds(new Set(printable.map(m => String(m.e251_MaterialId ?? m.e251_Title ?? ''))));
    }, [open, printable]);

    const toggle = (id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const handlePrint = () => {
        const selected = printable.filter(m =>
            selectedIds.has(String(m.e251_MaterialId ?? m.e251_Title ?? ''))
        );
        const printOptions = {
            title,
            supportingMaterials,
            lessonSteps: allLessonSteps,
            appendMaterials: selected,
            pageBreaksBetweenItems,
            printStepNotes
        };

        const html =
            printAllSteps || !currentStep
                ? buildAllInstructionStepsPrintHtml({
                      ...printOptions,
                      steps
                  })
                : buildInstructionStepPrintHtml({
                      ...printOptions,
                      step: currentStep
                  });

        printInstructionStepDocument(html, idToken);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Print lesson</DialogTitle>
                </DialogHeader>

                {canPrintCurrentStep && (
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor="print-all-steps" className="text-sm text-gray-700">
                            Print all steps
                        </label>
                        <Switch
                            id="print-all-steps"
                            checked={printAllSteps}
                            onCheckedChange={checked => setPrintAllSteps(checked === true)}
                        />
                    </div>
                )}

                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                        checked={printStepNotes}
                        onCheckedChange={checked => setPrintStepNotes(checked === true)}
                    />
                    <span>Print step notes</span>
                </label>

                <p className="text-sm text-gray-600">
                    {printAllSteps || !currentStep
                        ? 'All worksheets and readings across steps. Each selected material is included once at the end.'
                        : currentStep.e251_Summary || `Step ${currentStep.e251_StepNumber ?? ''}`}
                </p>

                <div>
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">Worksheets &amp; Readings</h6>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {printable.length === 0 ? (
                            <p className="text-sm text-gray-500">No worksheets or readings to print.</p>
                        ) : (
                            printable.map(material => {
                                const id = String(material.e251_MaterialId ?? material.e251_Title ?? '');
                                return (
                                    <label key={id} className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={selectedIds.has(id)}
                                            onCheckedChange={checked => toggle(id, checked === true)}
                                        />
                                        <span>{material.e251_Title || 'Material'}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>

                {printable.length > 0 && (
                    <>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="justify-start px-0 text-action-blue"
                            onClick={() => setSelectedIds(new Set())}
                        >
                            Unselect all
                        </Button>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <Checkbox
                                checked={pageBreaksBetweenItems}
                                onCheckedChange={checked => setPageBreaksBetweenItems(checked === true)}
                            />
                            <span>Use page breaks between items</span>
                        </label>
                    </>
                )}

                <DialogFooter>
                    <Button type="button" onClick={handlePrint}>
                        Print
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
